import { motion } from "framer-motion";
import { PORTRAIT } from "@/lib/assets";

const CHAPTERS = [
  {
    n: "01",
    title: "Heard and felt",
    body: "Luxury is not only seen. It is heard and felt. We craft the acoustic dimension of a brand with the same intention devoted to material, form and light — sound that carries meaning long after the moment has passed.",
  },
  {
    n: "02",
    title: "Bespoke by nature",
    body: "Akkas Music is a boutique creative agency focused on sound, music and emotional brand distinction. Every sonic identity is composed from the character of the brand itself — never templated, always considered.",
  },
  {
    n: "03",
    title: "Discreet by design",
    body: "Exceptional brands understand the power of sound. In keeping with the discreet nature of our work, selected references are shared personally, upon request.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="px-6 lg:px-14 py-28 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-3 border border-[#C5A880]/50" aria-hidden />
              <div className="group relative overflow-hidden bg-[#F3EFEA] aspect-[3/4]">
                <img
                  data-testid="founder-portrait-image"
                  src={PORTRAIT}
                  alt="Süleyman Akkas — Founder, Akkas Music"
                  className="h-full w-full object-cover object-center grayscale-[0.12] contrast-[1.02] transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span data-testid="founder-name" className="font-serif-display italic text-xl text-[#121110]">
                  Süleyman Akkas
                </span>
                <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8C857B]">
                  Founder &amp; Composer
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-mono-accent text-[11px] uppercase tracking-[0.4em] text-[#8C857B] mb-10"
          >
            02 / The Studio
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-[#121110] mb-12 max-w-[20ch]"
          >
            We help brands define how they should be <span className="italic text-[#8C857B]">heard and felt.</span>
          </motion.h2>

          <div className="flex flex-col divide-y divide-[#121110]/10 border-t border-[#121110]/10">
            {CHAPTERS.map((c, i) => (
              <motion.article
                key={c.n}
                data-testid={`manifesto-chapter-${i + 1}`}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="py-10 lg:py-14 grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-10"
              >
                <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C5A880] pt-2">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-serif-display font-light text-3xl sm:text-4xl leading-tight text-[#121110] mb-4">
                    {c.title}
                  </h3>
                  <p className="text-[#5C5650] text-sm sm:text-base leading-relaxed font-light max-w-xl">
                    {c.body}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
